import { Events } from './events';
import { Scene } from './scene';

// custom mime type used to identify a content-palette drag (as opposed to an
// OS file drag, which drop-handler.ts already handles on the same target)
const MIME_TYPE = 'application/x-simam-content';
const DRAG_ATTR = 'data-content-drag';

// wires up dragging a content-palette button (scene-panel.ts) into the
// viewport to place a new scene object at the drop point, instead of always
// adding new content at the scene centre. Must be registered on `target`
// BEFORE file-handler.ts's CreateDropHandler, since both attach capture-phase
// listeners to the same element and this relies on running first to steal
// its own drag/drop events via stopImmediatePropagation.
const registerContentDragDrop = (events: Events, scene: Scene, target: HTMLElement) => {
    const dragstart = (ev: DragEvent) => {
        const el = (ev.target as HTMLElement)?.closest?.(`[${DRAG_ATTR}]`);
        if (!el) {
            return;
        }
        const payload = el.getAttribute(DRAG_ATTR);
        ev.dataTransfer.effectAllowed = 'copy';
        ev.dataTransfer.setData(MIME_TYPE, payload);
        ev.stopImmediatePropagation();
    };

    const isContentDrag = (ev: DragEvent) => Array.from(ev.dataTransfer.types).includes(MIME_TYPE);

    const dragover = (ev: DragEvent) => {
        if (!isContentDrag(ev)) {
            return;
        }
        ev.preventDefault();
        ev.dataTransfer.dropEffect = 'copy';
        ev.stopImmediatePropagation();
    };

    const drop = (ev: DragEvent) => {
        if (!isContentDrag(ev)) {
            return;
        }
        ev.preventDefault();
        ev.stopImmediatePropagation();

        const raw = ev.dataTransfer.getData(MIME_TYPE);
        if (!raw) {
            return;
        }

        const payload = JSON.parse(raw);
        const rect = scene.canvas.getBoundingClientRect();
        const screenX = ev.clientX - rect.left;
        const screenY = ev.clientY - rect.top;

        events.fire('content.dropAt', payload, screenX, screenY);
    };

    target.addEventListener('dragstart', dragstart, true);
    target.addEventListener('dragover', dragover, true);
    target.addEventListener('drop', drop, true);
};

export { registerContentDragDrop };
